import { useQueryClient } from '@tanstack/react-query';
import { useMutation } from 'hooks/reactQuery';

import { useAppDispatch } from '@store/store';

import { setSnackbar } from '@slices/common';

import { setDefaultVendor, unsetDefaultVendor } from '@api/cmModuleCommon';

import { TSelectOptionType } from '@components/common/Select/Select.types';

import { ESnackbarTypes, EUserRoles } from '@enums/common';

import { CM_MODULE_VENDORS } from '@constants/queryKeys';
import { responseMessages } from '@constants/responseMessages';

interface IUseMutateSetCmDefaultVendorReturnType {
	setDefaultVendor: (option: TSelectOptionType) => void;
	isUpdatingDefaultVendor: boolean;
}

export const useMutateSetCmDefaultVendor = (): IUseMutateSetCmDefaultVendorReturnType => {
	const dispatch = useAppDispatch();
	const queryClient = useQueryClient();

	const { mutate, isLoading } = useMutation<TSelectOptionType>({
		mutationFn: (option: TSelectOptionType) => {
			return option?.value && (option.isDefault
				? unsetDefaultVendor({
					vendor: option.value as string,
				})
				: setDefaultVendor({
					vendor: option.value as string,
				}));
		},
		mutationKey: [CM_MODULE_VENDORS],
		onSuccess: () => {
			dispatch(setSnackbar({
				message: responseMessages.defaultVendorChanged,
				type: ESnackbarTypes.success,
			}));

			return queryClient.invalidateQueries([CM_MODULE_VENDORS]);
		},
		allowedRoles: [
			EUserRoles.networkOperatorCmModuleAdmin,
			EUserRoles.networkOperatorCmModuleEditor,
			EUserRoles.networkOperatorCmModuleViewer,
		],
	});

	return {
		setDefaultVendor: mutate,
		isUpdatingDefaultVendor: isLoading,
	};
};
